import {StyleInfo} from "lit/directives/style-map.js";
import {getReadableTextShade} from "@o2d/shared";

// Calibrated so the L* decision matches actual WCAG contrast parity for the
// app's dark-text token, --ha-color-dark: #272727 (theme-default.css,
// Anke's value — CSS ownership is hers, this constant is kept in sync with
// it, not the other way around). @o2d/shared's textbook default (50) assumes
// a symmetric black/white choice; #272727 is close to but not quite black,
// so the real break-even sits slightly higher (~54.2). Recalibrate here, not
// in the package, if theme-default.css's --ha-color-dark ever changes.
const LIGHTNESS_THRESHOLD_FOR_ROW_HEADER = 54;

// Warns at most once per distinct unparseable color value — this runs per
// visible row on every render of the virtualized timeline grid (e.g. on
// scroll), so an unguarded console.warn would flood the console for a single
// bad data point.
const warnedInvalidColors = new Set<string>();

/**
 * Resolves which CSS var reference (--ha-color-light / --ha-color-dark) gives
 * readable contrast against a hex color, or undefined when the value isn't a
 * parseable hex color — callers omit --text-color in that case (never
 * crashes the render), falling back to the inherited default text color.
 */
function resolveTextColorVar(color: string): string | undefined {
    try {
        const shade = getReadableTextShade(color, LIGHTNESS_THRESHOLD_FOR_ROW_HEADER);
        return shade === 'dark' ? 'var(--ha-color-dark)' : 'var(--ha-color-light)';
    } catch (error) {
        if (!warnedInvalidColors.has(color)) {
            warnedInvalidColors.add(color);
            console.warn(`[buildHeaderItemStyleMap] Unparseable color "${color}"`, error);
        }
        return undefined;
    }
}

/**
 * Builds the `--o2d-color-1` custom property for a row-header cell
 * (`HeaderItem.color`, sourced from the entity's own `Colorizable` color —
 * employee, project, etc.) so `timeline.css` can style the first Board/Timeline
 * column based on the entity's assigned color. Also sets `--text-color` to a
 * readable light/dark contrast choice against that same color (consumed via
 * `color: var(--text-color)` — the same mechanism the CSS itself already
 * uses, see timeline.css). Returns an empty style object
 * when the entity has no color, leaving the cell unstyled.
 */
export function buildHeaderItemStyleMap(color: string | null): StyleInfo {
    if (color === null) {
        return {};
    }

    const style: StyleInfo = {'--o2d-color-1': color};
    const textColorVar = resolveTextColorVar(color);
    if (textColorVar !== undefined) {
        style['--text-color'] = textColorVar;
    }

    return style;
}
